/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { useId } from 'react-aria';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import styles from './Modal.style';
import BaseModal from './BaseModal';
import messages from 'src/shared/app/base/l10n/uiL10n';
import PrimaryButton from 'src/shared/ui/component/button/PrimaryButton';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import InstructionText from 'src/shared/ui/component/text/InstructionText';
import CloseButton from 'src/shared/ui/component/button/CloseButton';
import classNames from 'classnames';

type Props = {
  title?: string,
  icon?: React.Node,
  message?: string | React.Node | [string | React.Node],
  content?: string | React.Node,
  handleAccept?: () => void,
  handleClose: () => void,
  acceptCTA?: string | React.Node,
  closeCTA?: string | React.Node,
  hideActions?: boolean,
  showClose?: boolean,
  width?: string | number,
  isOpen?: boolean,
  isAnimated?: boolean,
  withLargeTitle?: boolean,
  shouldCloseOnOverlayClick?: boolean,
  onAfterClose?: () => void,
  titleClassName?: string,
  contentClassName?: string,
  // Self passed
  intl: intlShape,
};

const useStyles = createUseThemeStyles(styles);

const Modal = ({
  title,
  icon,
  message,
  content,
  handleClose,
  handleAccept,
  acceptCTA,
  closeCTA,
  hideActions = false,
  showClose = false,
  width,
  withLargeTitle = false,
  intl,
  isOpen,
  isAnimated = false,
  shouldCloseOnOverlayClick = false,
  titleClassName,
  contentClassName,
  onAfterClose,
}: Props): React.Node => {
  const classes = useStyles({ withLargeTitle });

  // Elements
  const iconElt = icon && <div className={classes.icon}>{icon}</div>;
  const acceptCtaIsNode = acceptCTA && typeof acceptCTA !== 'string';
  const closeCtaIsNode = closeCTA && typeof closeCTA !== 'string';

  const titleId = useId();
  const titleElt = title && (
    <h2 id={titleId} className={classNames(classes.title, titleClassName)}>
      {title}
    </h2>
  );

  const messageItem = (messageItem) => (
    <InstructionText layoutClass={classes.message}>
      {messageItem}
    </InstructionText>
  );

  const messageElt = message && (
    <>
      {Array.isArray(message) ? (
        <>{message.map(messageItem)}</>
      ) : (
        <>{messageItem(message)}</>
      )}
    </>
  );
  const messageId = useId();

  const confirmBtn = handleAccept && (
    <PrimaryButton
      layoutClass={classes.button}
      onClick={handleAccept}
      hasFocus
      data-testid="modal-primary-cta"
    >
      {acceptCTA || intl.formatMessage(messages.confirm)}
    </PrimaryButton>
  );

  const contentId = useId();

  return (
    <BaseModal
      width={width}
      isOpen={isOpen}
      isAnimated={isAnimated}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      handleClose={handleClose}
      onAfterClose={onAfterClose}
      ariaLabelledBy={title ? titleId : message ? messageId : undefined}
      ariaDescribedBy={
        title && message ? messageId : content ? contentId : undefined
      }
      contentClassName={contentClassName}
    >
      {iconElt}
      {titleElt}
      {showClose && (
        <CloseButton
          layoutClass={classes.closeCtn}
          onClick={handleClose}
          data-testid="modal-close-btn"
        />
      )}
      {message && <div id={messageId}>{messageElt}</div>}
      {content && (
        <div id={contentId} className={classes.content}>
          {content}
        </div>
      )}
      {!hideActions && (
        <div className={classNames(classes.actions)}>
          {closeCtaIsNode ? (
            <div className={classes.button}>{closeCTA}</div>
          ) : (
            <SecondaryButton
              layoutClass={classes.button}
              onClick={handleClose}
              autoFocus={!handleAccept}
              data-testid="modal-secondary-cta"
            >
              {closeCTA || intl.formatMessage(messages.ok)}
            </SecondaryButton>
          )}
          {acceptCtaIsNode ? (
            <div className={classes.button}>{acceptCTA}</div>
          ) : (
            confirmBtn
          )}
        </div>
      )}
    </BaseModal>
  );
};

export default compose(injectIntl)(Modal);
